import CheckIcons from '@/icons/CheckIcons'
import { useTranslation } from 'react-i18next'
import BusinessTripsSection from './BusinessTripsSection'
import ContractServicesSection from './ContractServicesSection'
import { isSystemNote } from './helpers'
import JourneyStepNotes from './JourneyStepNotes'
import NegotiationsSection from './NegotiationsSection'
import POCServiceCard from './POCServiceCard'
import StepActions from './StepActions'
import { StepColumnProps } from './types'

const StepColumn = ({
    step,
    stepIndex,
    status,
    journey,
    customerId,
    isLocked,
    isCompleted,
    outcome,
    linkedBusinesses,
    linkedTickets,
    unlinkedBusinesses,
    isLinkingBusiness,
    pocServices,
    negotiations,
    contractServices,
    hasContractCompleted,
    onMoveToStep,
    onAddNote,
    onLinkBusiness,
    onCreateBusinessTrip,
    onCreateMeetingOnline,
    onCreatePOC,
    onAddNegotiation,
    onCreateContract,
    onUpdatePOCResult,
    onPOCRetention,
    onConvertPOCToOfficial,
    pendingPocConvertServiceId,
}: StepColumnProps) => {
    const { t } = useTranslation()
    const isStepHeaderClickable = !isLocked && outcome === 'active'
    const handleStepHeaderClick = () => {
        if (!isStepHeaderClickable) return
        onMoveToStep(step.value)
    }
    const handleStepHeaderKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement>
    ) => {
        if (!isStepHeaderClickable) return

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onMoveToStep(step.value)
        }
    }

    const stepBadgeClassName =
        status === 'current'
            ? 'bg-blue-500 text-white'
            : status === 'completed'
              ? 'bg-emerald-500 text-white'
              : 'bg-gray-200 text-gray-500'
    const stepLabelClassName =
        status === 'current'
            ? 'text-blue-700'
            : status === 'completed'
              ? 'text-emerald-700'
              : 'text-gray-600'

    return (
        <div
            className={`min-h-[200px] flex flex-col ${
                status === 'current'
                    ? 'border-2 border-dashed border-blue-300'
                    : 'border border-gray-100'
            }`}
            data-tour={`step-${step.value}`}
        >
            {/* Step Header */}
            <div
                className="px-3 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50/50"
                role="button"
                tabIndex={isStepHeaderClickable ? 0 : -1}
                aria-disabled={!isStepHeaderClickable}
                onClick={handleStepHeaderClick}
                onKeyDown={handleStepHeaderKeyDown}
            >
                <div className="flex items-center gap-2">
                    <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${stepBadgeClassName}`}
                    >
                        {status === 'completed' ? (
                            <CheckIcons className="w-3 h-3" />
                        ) : (
                            stepIndex + 1
                        )}
                    </span>
                    <span
                        className={`text-xs font-medium ${stepLabelClassName}`}
                    >
                        {step.label}
                    </span>
                </div>
                {status === 'current' && (
                    <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                )}
            </div>

            {/* Notes List */}
            <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[300px]">
                <JourneyStepNotes
                    customerId={customerId}
                    journeyId={journey.customer_journey_id}
                    step={step.value}
                    isSystemNote={isSystemNote}
                />
            </div>

            {/* Business Trips Info - Show in Presentation step */}
            {step.value === 'presentation' && (
                <BusinessTripsSection
                    linkedBusinesses={linkedBusinesses}
                    linkedTickets={linkedTickets}
                    unlinkedBusinesses={unlinkedBusinesses}
                    isCompleted={isCompleted}
                    isLinkingBusiness={isLinkingBusiness}
                    onLinkBusiness={onLinkBusiness}
                />
            )}

            {/* POC Services Info - Show in POC step */}
            {step.value === 'poc' && pocServices.length > 0 && (
                <div className="p-2 border-t border-gray-100 space-y-2">
                    <div className="text-[10px] font-medium text-amber-700 uppercase">
                        {t('poc-services')} ({pocServices.length})
                    </div>
                    {pocServices.map((note) => (
                        <POCServiceCard
                            key={note.customer_journey_note_id}
                            note={note}
                            isLocked={isLocked}
                            isCompleted={isCompleted}
                            isConversionPending={
                                pendingPocConvertServiceId ===
                                Number(note.service_poc?.service_id)
                            }
                            onUpdateResult={onUpdatePOCResult}
                            onRetention={onPOCRetention}
                            onConvert={onConvertPOCToOfficial}
                        />
                    ))}
                </div>
            )}

            {/* Negotiations Info */}
            {negotiations.length > 0 && (
                <NegotiationsSection negotiations={negotiations} />
            )}

            {/* Contract Services - Show in Contract step */}
            {step.value === 'contract' && (
                <ContractServicesSection contractServices={contractServices} />
            )}

            {/* Step Actions - Only show for active journeys */}
            {!isCompleted && (
                <StepActions
                    stepValue={step.value}
                    isLocked={isLocked}
                    hasContractCompleted={hasContractCompleted}
                    onAddNote={onAddNote}
                    onCreateBusinessTrip={onCreateBusinessTrip}
                    onCreateMeetingOnline={onCreateMeetingOnline}
                    onCreatePOC={onCreatePOC}
                    onAddNegotiation={onAddNegotiation}
                    onCreateContract={onCreateContract}
                />
            )}
        </div>
    )
}

export default StepColumn
